import React, { useMemo } from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Select,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useFormActions, useForm, useMeta, useValidator } from "@/hooks";
import { incrementRateType } from "../../config/constant";
import { toCurrency } from "../../utils/utils";
import { currencyMask } from "../../utils/maskInput";
import {
    MUTATION_UPDATE_PRICE_TABLE_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const PriceTableForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, inputChange, selectChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const isAmount = input => {
        return input.increment_type === "amount" && input.rate;
    };

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                rate: isAmount(inputs)
                    ? toCurrency(inputs.rate)
                    : parseInt(inputs.rate, 10),
                type: "PriceTable",
            },
        },
    };

    const { isValid, isSubmited } = useValidator(
        [
            {
                field: "rate",
                method: "isEmpty",
                validWhen: false,
                message: "This rate is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Price Table",
        "",
        MUTATION_UPDATE_PRICE_TABLE_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const typeOptions = useMemo(
        () =>
            incrementRateType.map(option => ({
                ...option,
                value: String(option.value),
            })),
        [],
    );

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Price Table</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Price Table Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Price Table actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Information">
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Select
                                fullWidth
                                label="Increment Rate Type"
                                onValueChange={value =>
                                    selectChange({ value }, "increment_type")
                                }
                                options={typeOptions}
                                value={
                                    inputs.increment_type != null
                                        ? String(inputs.increment_type)
                                        : undefined
                                }
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            {inputs.increment_type === "PERCENTAGE" ? (
                                <Input
                                    fullWidth
                                    label="Rate"
                                    name="rate"
                                    onChange={inputChange}
                                    prefix="%"
                                    type="number"
                                    value={inputs.rate || ""}
                                />
                            ) : null}
                            {inputs.increment_type === "amount" ? (
                                <Input
                                    fullWidth
                                    label="Rate"
                                    mask={currencyMask(inputs.rate || "")}
                                    name="rate"
                                    onChange={inputChange}
                                    prefix="$"
                                    value={inputs.rate || ""}
                                />
                            ) : null}
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default PriceTableForm;
